import {
  FC,
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import clsx from "classnames";
import { useStyles } from "./DailyReward.style";
import { Dialog } from "../../core/Dialog/Dialog";
import Button from "../../core/Button/Button";
import {
  Typography,
  TypographyColor,
  TypographyVariant,
} from "../../core/Typography/Typography";
import {
  claimLoginRewardGQL,
  loginRewardsGQL,
  LoginRewardStatus,
  meGQL,
} from "@onmo/graphql/graphql";
import { LoginReward } from "@onmo/graphql/graphql";
import { DailyCard } from "./DailyCard";
import { getCurrentDailyReward } from "../../models/dailyReward";
import { useMutation, useQuery } from "@apollo/client";
import { DailyStreakAnimation } from "./DailyStreakAnimation/DailyStreakAnimation";
import { useTranslation } from "react-i18next";

interface DailyRewardProps {
  onClosePopup?: () => void;
  onOpenPopup?: () => void;
  open: boolean;
  loginAction?: () => Promise<any>;
}
export const DailyReward: FC<
  HTMLAttributes<HTMLDivElement> & DailyRewardProps
> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { className, onClosePopup, onOpenPopup, open, loginAction } = props;
  const [isLoginFinished, setIsLoginFinished] = useState(false);
  const { data: loginRewardsData } = useQuery(loginRewardsGQL, {
    fetchPolicy: "network-only",
    skip: !isLoginFinished && !!loginAction,
  });
  const loginRewards = loginRewardsData?.loginRewards;
  const activeRewardIndex = getCurrentDailyReward(loginRewards);

  const currentReward = useMemo(
    () => loginRewards?.[activeRewardIndex],
    [loginRewards, activeRewardIndex],
  );

  const isActiveLastReward = useMemo(
    () => activeRewardIndex === loginRewards?.length - 1,
    [loginRewards, activeRewardIndex],
  );

  const [claimLoginRewardMutation] = useMutation(claimLoginRewardGQL);

  const claimLoginReward = useCallback(async () => {
    if (
      loginRewards?.[activeRewardIndex]?.claimStatus !==
      LoginRewardStatus.Unclaimed
    )
      return;

    try {
      claimLoginRewardMutation({
        variables: {
          day: activeRewardIndex,
        },
        refetchQueries: [{ query: meGQL }],
      });
    } catch (error) {
      console.error(error);
    }
  }, [activeRewardIndex, claimLoginRewardMutation, loginRewards]);

  const onClose = () => {
    onClosePopup?.();
    claimLoginReward();
  };

  useEffect(() => {
    if (currentReward?.claimStatus !== LoginRewardStatus.Unclaimed) return;
    onOpenPopup?.();
  }, [currentReward]);

  useEffect(() => {
    if (!loginAction) return;
    setIsLoginFinished(false);
    (async () => {
      await loginAction();
      setIsLoginFinished(true);
    })();
  }, [loginAction]);

  if (!loginRewards?.length) return null;

  return (
    <Dialog
      className={clsx(classes.root, classes.rootAnimation, className)}
      open={open}
    >
      <DailyStreakAnimation
        streak={activeRewardIndex}
        reward={currentReward?.amount}
      />
      <div className={classes.content}>
        <div className={classes.title}>
          <Typography
            variant={TypographyVariant.HeadingH4Bold}
            color={TypographyColor.Text1}
          >
            {t("common_Daily Reward")}
          </Typography>
          <Typography
            variant={TypographyVariant.BodyB5Regular}
            color={TypographyColor.Text2}
          >
            {t("common_Come back tomorrow for more rewards")}
          </Typography>
        </div>
        <div className={classes.gridContent}>
          {loginRewards.map((reward: LoginReward, index: number) => {
            if (index === 6) return null;
            return (
              <DailyCard
                key={index}
                reward={reward}
                day={index + 1}
                id={`dailyCard-${index}`}
                active={index === activeRewardIndex}
              />
            );
          })}
        </div>
        <DailyCard
          reward={loginRewards[6]}
          day={loginRewards.length}
          fullWidth
          id={`dailyCard-6`}
          active={isActiveLastReward}
        />
        <Button fullWidth className={classes.buttonAnimation} onClick={onClose}>
          {t("common_Continue")}
        </Button>
      </div>
    </Dialog>
  );
};
